/**
 * @file Unions de chaines + tableaux const pour tous les identifiants fermes du jeu (grades, modes, monnaies, stats...).
 */

// ---- Grades d'armes (13) -------------------------------------------------------------------
export const GRADES = ['D', 'C', 'B', 'A', 'S', 'SS', 'SSS', 'X', 'XX', 'XXX', 'Z', 'ZZ', 'ZZZ'] as const;
export type GradeId = (typeof GRADES)[number];
/** Index 0..12 d'un grade (ordre croissant de puissance). */
export const gradeIndex = (g: GradeId): number => GRADES.indexOf(g);

// ---- Comportements d'armes (7) -------------------------------------------------------------
export const WEAPON_BEHAVIORS = ['single', 'pierce', 'shotgun', 'explosive', 'laser', 'missile', 'blastSniper'] as const;
export type WeaponBehavior = (typeof WEAPON_BEHAVIORS)[number];

// ---- Biomes --------------------------------------------------------------------------------
// Themes de stage (un par stage, en rotation) : foret, desert, toundra, automne, cerisiers.
export const BIOMES = ['forest', 'desert', 'tundra', 'autumn', 'blossom'] as const;
export type BiomeId = (typeof BIOMES)[number];

// ---- Modes de jeu --------------------------------------------------------------------------
export const MODE_IDS = ['main', 'speedRun', 'mine', 'boss', 'monster', 'raid', 'adventure', 'fish', 'harvest', 'hub'] as const;
export type ModeId = (typeof MODE_IDS)[number];

// ---- Monnaies et ressources (toutes gerees par le Wallet) ------------------------------------
export const CURRENCIES = ['money', 'ruby', 'dia', 'petCoin', 'starGem', 'coreForest', 'coreDesert', 'coreTundra'] as const;
export type CurrencyId = (typeof CURRENCIES)[number];
/** Ressources de recolte (bois/pierre/minerais) : distinctes de l'argent, destinees au craft futur. */
export const RESOURCES = ['wood', 'stone', 'ironOre', 'copperOre', 'silverOre', 'goldOre', 'crystal'] as const;
export type ResourceId = (typeof RESOURCES)[number];
export type WalletId = CurrencyId | ResourceId;
export const WALLET_IDS: readonly WalletId[] = [...CURRENCIES, ...RESOURCES];

// ---- Stats nommees (systeme de modificateurs unifie) ----------------------------------------
export const STAT_IDS = [
  'damage', // multiplicateur de degats global
  'attackSpeed', // multiplicateur de cadence
  'money', // multiplicateur de gains d'argent
  'critChance', // probabilite 0..1 (additif)
  'critDamage', // multiplicateur de critique (base 2)
  'moveSpeed', // vitesse de deplacement u/s
  'range', // multiplicateur de portee
  'offlineHours', // plafond de gains hors-ligne (heures, additif)
  'offlineRate', // efficacite hors-ligne (multiplicateur)
  'rebirthGain', // multiplicateur de Ruby gagnes
  'diaGain', // multiplicateur de gemmes gagnees
  'petCoinGain',
  'xpGain', // XP de passe / aventure
  'harvestYield', // multiplicateur de ressources recoltees
  'rareTreeChance', // multiplicateur de chance d'arbres gold/rose
] as const;
export type StatId = (typeof STAT_IDS)[number];

// ---- Cibles / entites ------------------------------------------------------------------------
export const TARGET_FAMILIES = ['tree', 'rock', 'ore', 'monster', 'boss', 'decor'] as const;
export type TargetFamily = (typeof TARGET_FAMILIES)[number];

/** Types d'ennemis de l'original (Enemy.Type), conserves comme etiquette de compatibilite/stats. */
export type LegacyEnemyType = 'Tree' | 'Tree_SpeedRun' | 'Monster' | 'BossMonster' | 'Ore' | 'RaidBoss';

export type EntityKind = 'cat' | 'target' | 'projectile' | 'pickup' | 'fx';

// ---- Rarete ----------------------------------------------------------------------------------
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

// ---- Upgrades principaux ---------------------------------------------------------------------
export const UPGRADE_IDS = ['power', 'speed', 'money'] as const;
export type UpgradeId = (typeof UPGRADE_IDS)[number];

// ---- Divers ----------------------------------------------------------------------------------
export type Locale = 'fr' | 'en';
export type ControlOverride = 'player' | 'forceOn' | 'forceOff';
